import { useState, useEffect } from "react";
import { partyStructureSchemas, type partyStructureTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { v7 as uuidv7 } from "uuid";
import { type Uuid } from "@pineseed/spec";

type ParticipantGroup = partyStructureTypes["ParticipantGroup"];
type View = 'groups' | 'stats';

const LAST_GROUP_VIEW_KEY = 'admin-last-group-view';

export function GroupPanel() {
  const { adminApp, group } = useAdmin();

  const [view, setView] = useState<View>(() => {
    const saved = localStorage.getItem(LAST_GROUP_VIEW_KEY) as View | null;
    return saved || 'groups';
  });

  // Persist view
  useEffect(() => {
    localStorage.setItem(LAST_GROUP_VIEW_KEY, view);
  }, [view]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ParticipantGroup | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  // CRUD handlers
  const handleCreate = async (data: Partial<ParticipantGroup>) => {
    try {
      await group.createEntity({
        ...data,
        id: uuidv7() as Uuid,
        workspaceId: adminApp.workspaceId as Uuid,
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create group:', error);
      throw error;
    }
  };

  const handleEdit = async (data: Partial<ParticipantGroup>) => {
    if (!editingGroup) return;
    try {
      await group.updateEntity(editingGroup.id, data);
      setEditingGroup(null);
    } catch (error) {
      console.error('Failed to update group:', error);
      throw error;
    }
  };

  const handleDelete = async (g: ParticipantGroup) => {
    if (!confirm(`Delete group "${g.name}"?\n\nThis will not delete members, only the group.`)) return;
    try {
      await group.deleteEntity(g.id);
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  // Load members for expanded group
  const handleToggleExpand = async (groupId: string) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
    } else {
      setExpandedGroupId(groupId);
      if (!group.groupMembers[groupId]) {
        await group.loadGroupMembers(groupId);
      }
    }
  };

  // Get statistics
  const stats = group.getStats();

  // Columns definition
  const groupColumns: Column<ParticipantGroup>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (g) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{g.name}</div>
          {g.description && (
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{g.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'members',
      label: 'Members',
      render: (g) => {
        const members = group.groupMembers[g.id] || [];
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {members.length}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(g.id);
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              {expandedGroupId === g.id ? 'Hide' : 'View'} Members
            </button>
          </div>
        );
      },
    },
    {
      key: 'workspaceId',
      label: 'Workspace',
      render: (g) => (
        <span className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate max-w-xs block">
          {g.workspaceId.slice(0, 12)}...
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (g) => (
        <span className="text-xs text-gray-500">{new Date(g.createdAt).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Group Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage participant groups across the workspace
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create Group
        </button>
      </div>

      {/* View Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {(['groups', 'stats'] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 border-b-2 transition-colors ${view === v
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
              {v === 'groups' && <span className="ml-2 text-xs">({stats.total})</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Stats View */}
      {view === 'stats' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Overview
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Groups:</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{stats.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Members:</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{stats.totalMembers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Avg. Members/Group:</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{stats.averageMembersPerGroup}</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Group Sizes
            </h3>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {group.entities.map((g) => {
                const memberCount = group.groupMembers[g.id]?.length || 0;
                const maxMembers = Math.max(...group.entities.map(grp => group.groupMembers[grp.id]?.length || 0), 1);
                const percentage = (memberCount / maxMembers) * 100;

                return (
                  <div key={g.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-900 dark:text-gray-100 font-medium truncate">{g.name}</span>
                      <span className="text-gray-600 dark:text-gray-400">{memberCount}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Groups Table */}
      {view === 'groups' && (
        <div className="space-y-2">
          <EntityTable
            data={group.entities}
            columns={groupColumns}
            onEdit={setEditingGroup}
            onDelete={handleDelete}
            loading={group.loading}
            emptyMessage="No groups found. Create your first group to get started."
          />

          {/* Expanded Group Members */}
          {expandedGroupId && group.groupMembers[expandedGroupId] && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Members of "{group.entities.find(g => g.id === expandedGroupId)?.name}"
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.groupMembers[expandedGroupId].length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 col-span-full">
                    No members in this group yet.
                  </p>
                ) : (
                  group.groupMembers[expandedGroupId].map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-3 p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700"
                    >
                      {member.avatar && (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {member.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {member.type} • {member.status}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Group"
      >
        <EntityForm
          schema={partyStructureSchemas.CreateParticipantGroupRequest}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Group Modal */}
      <Modal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        title="Edit Group"
      >
        {editingGroup && (
          <EntityForm
            schema={partyStructureSchemas.UpdateParticipantGroupRequest}
            initialData={editingGroup}
            onSubmit={handleEdit}
            onCancel={() => setEditingGroup(null)}
            submitLabel="Update"
          />
        )}
      </Modal>
    </div>
  );
}

